import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authService } from '../services/auth';
import { responsesService } from '../services/questionnaire';
import { queryClient } from '../lib/queryClient';
import logger from '../utils/logger';

const AuthContext = createContext(null);

// Sync localStorage questionnaire data to backend. Hoisted outside the
// component because it only touches localStorage + service — no React state.
async function syncLocalStorageData() {
  try {
    const localData = localStorage.getItem('awfm_questionnaire_progress');
    if (!localData) return;
    const progressData = JSON.parse(localData);
    if (!progressData.responses || Object.keys(progressData.responses).length === 0) {
      return;
    }
    const responses = Object.entries(progressData.responses).map(([key, value]) => ({
      question: progressData.questionId || 'Q10A',
      checkpoint_number_input: parseInt(key.replace('q', '')),
      selected_option_ids: Array.isArray(value) ? value : [value],
    }));
    await responsesService.bulkSave(responses);
    localStorage.removeItem('awfm_questionnaire_progress');
    logger.log('Successfully synced localStorage data to backend');
  } catch (error) {
    logger.error('Failed to sync localStorage data:', error);
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [tokens, setTokens] = useState(() => {
    // Load tokens from localStorage on init
    const savedTokens = localStorage.getItem('authTokens');
    return savedTokens ? JSON.parse(savedTokens) : null;
  });

  // Save tokens to localStorage whenever they change
  useEffect(() => {
    if (tokens) {
      localStorage.setItem('authTokens', JSON.stringify(tokens));
    } else {
      localStorage.removeItem('authTokens');
    }
  }, [tokens]);

  // Listen for session expiry events from API interceptor
  useEffect(() => {
    const handleSessionExpired = () => {
      logger.log('Session expired - logging out user');
      setTokens(null);
      setUser(null);
      setSessionExpired(true);
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, []);

  // Load user profile whenever we have an access token (mount + SSO callback)
  useEffect(() => {
    const loadUser = async () => {
      if (tokens?.access) {
        try {
          const userData = await authService.getProfile();
          setUser(userData);
        } catch (error) {
          logger.error('Failed to load user:', error);
          setTokens(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    loadUser();
  }, [tokens?.access]);

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  const login = useCallback(async (email, password) => {
    setIsLoggingIn(true);
    setSessionExpired(false);
    try {
      const data = await authService.login(email, password);
      setTokens(data.tokens);
      setUser(data.user);
      try {
        const fullProfile = await authService.getProfile();
        setUser(fullProfile);
      } catch (_) { /* user still has basic data from login */ }
      await syncLocalStorageData();
      return data;
    } finally {
      setIsLoggingIn(false);
    }
  }, []);

  const register = useCallback(async (email, password, displayName, isHcw, options = {}) => {
    const data = await authService.register(email, password, displayName, isHcw, options);
    setTokens(data.tokens);
    setUser(data.user);
    try {
      const fullProfile = await authService.getProfile();
      setUser(fullProfile);
    } catch (_) { /* user still has basic data from register */ }
    await syncLocalStorageData();
    return data;
  }, []);

  const logout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      if (tokens?.refresh) {
        await authService.logout(tokens.refresh);
      }
      setTokens(null);
      setUser(null);
      // Clear all cached query data to prevent data leaking to next user
      queryClient.clear();
    } finally {
      setIsLoggingOut(false);
    }
  }, [tokens?.refresh]);

  const loginWithGoogle = useCallback(async (accessToken, idToken) => {
    setIsLoggingIn(true);
    try {
      const data = await authService.googleAuth(accessToken, idToken);
      setTokens(data.tokens);
      setUser(data.user);
      try {
        const fullProfile = await authService.getProfile();
        setUser(fullProfile);
      } catch (_) { /* user still has basic data from auth */ }
      await syncLocalStorageData();
      return data;
    } finally {
      setIsLoggingIn(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await authService.getProfile();
      setUser(userData);
      return userData;
    } catch (error) {
      logger.error('Failed to refresh user:', error);
      throw error;
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      tokens,
      loading,
      isLoggingIn,
      isLoggingOut,
      sessionExpired,
      login,
      register,
      logout,
      loginWithGoogle,
      refreshUser,
      clearSessionExpired,
      setTokens,
    }),
    [
      user,
      tokens,
      loading,
      isLoggingIn,
      isLoggingOut,
      sessionExpired,
      login,
      register,
      logout,
      loginWithGoogle,
      refreshUser,
      clearSessionExpired,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
